import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { PreviousPeriodMeta } from '../models/models';
import {
  preparationTypeDisplay,
  reportingIntervalDisplay,
  scenarioDisplay,
} from '../spreading-column-header-display';

/**
 * Slide-out filter panel for the "previous periods in data categorization"
 * feature. Lists every candidate prior period and lets the user toggle
 * which to display in the Financial Template grid.
 *
 * Apply Filters is disabled until the local selection differs from the
 * inbound `selectedKeys`. The "X" button and outside-click both close
 * without applying (revert to inbound state).
 */
@Component({
  selector: 'app-previous-periods-filter-panel',
  templateUrl: './previous-periods-filter-panel.component.html',
  styleUrls: ['./previous-periods-filter-panel.component.scss'],
  standalone: false
})
export class PreviousPeriodsFilterPanelComponent implements OnChanges {
  readonly reportingIntervalDisplay = reportingIntervalDisplay;
  readonly scenarioDisplay = scenarioDisplay;
  readonly preparationTypeDisplay = preparationTypeDisplay;

  @Input() availablePeriods: Array<PreviousPeriodMeta> = [];
  @Input() selectedKeys: Array<string> = [];
  // Optional trigger element (e.g., the "Filter Periods" link). Clicks
  // landing inside it are ignored by the outside-click listener so the
  // toggle's own click handler can run without the panel auto-closing
  // and discarding any in-progress local edits.
  @Input() triggerEl?: HTMLElement;

  @Output() applyFilters = new EventEmitter<Array<string>>();
  @Output() closePanel = new EventEmitter<void>();

  // Local working copy; only emitted to parent on Apply.
  localSelected: Set<string> = new Set();
  // Cached snapshot of the inbound `selectedKeys` so `hasChanges` can be
  // computed in O(1) on each toggle instead of allocating two Sets per
  // change-detection pass (Angular calls getters very often).
  private inboundSelected: Set<string> = new Set();
  hasChanges = false;

  constructor(
      private hostEl: ElementRef<HTMLElement>,
      private _crd: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedKeys']) {
      this.inboundSelected = new Set(this.selectedKeys || []);
      this.localSelected = new Set(this.inboundSelected);
      this.hasChanges = false;
      this._crd.detectChanges();
    }
  }

  isChecked(periodKey: string): boolean {
    return this.localSelected.has(periodKey);
  }

  toggle(periodKey: string): void {
    if (this.localSelected.has(periodKey)) {
      this.localSelected.delete(periodKey);
    } else {
      this.localSelected.add(periodKey);
    }
    this.hasChanges = !this._setsEqual(this.localSelected, this.inboundSelected);
    this._crd.detectChanges();
  }

  private _setsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const k of a) {
      if (!b.has(k)) return false;
    }
    return true;
  }

  onApply(): void {
    if (!this.hasChanges) return;
    this.applyFilters.emit(Array.from(this.localSelected));
  }

  onClose(): void {
    this.closePanel.emit();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedown(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target) return;
    if (this.hostEl.nativeElement.contains(target)) return;
    if (this.triggerEl && this.triggerEl.contains(target)) return;
    this.closePanel.emit();
  }
}
